import * as React from 'react';

import { getMouseCanvas, GenericChartComponent, isDefined } from '@react-financial-charts/core';
import type { MoreProps } from '@/types/commontypes';

export interface ClickableCircleProps {
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly strokeWidth: number;
  readonly strokeStyle: string;
  readonly fillStyle: string;
  readonly r: number;
  readonly cx?: number;
  readonly cy?: number;
  readonly className: string;
  readonly show: boolean;
  readonly interactiveCursorClass?: string;
  readonly xyProvider?: (moreProps: MoreProps) => number[];
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
}

export class ClickableCircle extends React.Component<ClickableCircleProps> {
  public static defaultProps = {
    className: 'react-financial-charts-interactive-line-edge',
    show: false,
  };

  public render() {
    const {
      interactiveCursorClass,
      onDragStart,
      onDrag,
      onDragComplete,
      show,
      onDoubleClick: _onDoubleClick,
    } = this.props;

    if (!show) {
      return null;
    }

    return (
      <GenericChartComponent
        interactiveCursorClass={interactiveCursorClass}
        selected
        isHover={this.isHover}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getMouseCanvas}
        onDoubleClick={this.handleDoubleClick}
        drawOn={['pan', 'mousemove', 'drag']}
      />
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    ctx.save();
    const { strokeStyle, strokeWidth, fillStyle, r, cx, cy } = this.props;

    ctx.lineWidth = strokeWidth;
    ctx.fillStyle = fillStyle;
    ctx.strokeStyle = strokeStyle;

    const [x, y] = this.helper(moreProps);
    const {
      chartConfig: { height },
    } = moreProps;

    ctx.beginPath();
    if (!isDefined(cx) && isDefined(cy)) {
      const xx = 20; // 좌측 고정
      ctx.arc(xx, y, r, 0, 2 * Math.PI, false);
    } else if (!isDefined(cy) && isDefined(cx)) {
      const bottomOffsetFraction = 0.2; // 20% from bottom
      const yy = height * (1 - bottomOffsetFraction);
      ctx.arc(x, yy, r, 0, 2 * Math.PI, false);
    } else {
      ctx.arc(x, y, r, 0, 2 * Math.PI, false);
    }
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  };

  private readonly isHover = (moreProps: MoreProps) => {
    const { mouseXY } = moreProps;
    const { cx, cy } = this.props;
    const {
      chartConfig: { height },
    } = moreProps;

    // 마우스가 차트 섹션 밖이면 호버 불가 (서브차트 간 이동 시 호버 고착 방지)
    if (mouseXY[1] < 0 || mouseXY[1] > height) return false;

    const r = this.props.r + 7;
    const [_x, y] = this.helper(moreProps);

    // cx 없고 cy 있는 경우: drawOnCanvas와 동일하게 x=20 고정
    const x = !isDefined(cx) && isDefined(cy) ? 20 : _x;

    const [mx, my] = mouseXY;
    const hover = x - r < mx && mx < x + r && y - r < my && my < y + r;

    return hover;
  };

  private readonly helper = (moreProps: MoreProps) => {
    const { xyProvider, cx, cy } = this.props;

    if (xyProvider !== undefined) {
      return xyProvider(moreProps);
    }

    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    const x = xScale(cx ?? 0);
    const y = yScale(cy ?? 0);
    return [x, y];
  };

  private readonly handleDoubleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    if (this.isHover(moreProps)) {
      const { onDoubleClick } = this.props;
      if (onDoubleClick) {
        onDoubleClick(e, moreProps);
      }
    }
  };
}
